import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// THE RIGHT-HAND COLUMN, BOTTOM UP (2026-10-31).
//
// Khari, on a screenshot of the barista dock: *"in our UI, these overlap.
// radio, chat, 'done' button when sitting (and sometimes driving)"*. The player
// bar (PlayerBar) already publishes its height as --hud-player-h so things can
// stand on it; the shop dock ignored it and sat over the radio, and the seated
// Done button ignored both and sat on the dock's Send.
//
// So the column has one order, and each layer publishes its height for the
// next one up:
//
//   Done / the speed readout     bottom: player + dock
//   a chat dock, or the chat's   --hud-dock-h  (this hook)
//     recent lines
//   the player bar               --hud-player-h (PlayerBar)
//
// Whatever is open in that middle layer measures itself here; the tallest one
// wins (two docks open at once is a bug elsewhere, but the column still holds).
// Gone the moment it unmounts, so nothing floats over empty space.
//
// --hud-corner-h (Hud) is the top-right corner the minimap takes. Docks cap
// their height with it (DOCK_MAX_H) so the column never climbs into the map,
// and leave a row for Done above them.

const GAP = 10;
const slots = new Map();
let seq = 0;

function publish() {
  if (typeof document === 'undefined') return;
  const h = slots.size ? Math.max(...slots.values()) : 0;
  const root = document.documentElement;
  if (h > 0) root.style.setProperty('--hud-dock-h', `${Math.ceil(h) + GAP}px`);
  else root.style.removeProperty('--hud-dock-h');
}

/** A ref for something in the column's middle layer: its height becomes --hud-dock-h while it is mounted. */
export function useStackSlot() {
  const id = useRef(0);
  if (!id.current) id.current = ++seq;
  const watch = useRef(null);
  const ref = useCallback((el) => {
    watch.current?.disconnect();
    watch.current = null;
    if (!el) { slots.delete(id.current); publish(); return; }
    const measure = () => { slots.set(id.current, el.getBoundingClientRect().height); publish(); };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      watch.current = new ResizeObserver(measure);
      watch.current.observe(el);
    }
  }, []);
  useEffect(() => () => { watch.current?.disconnect(); slots.delete(id.current); publish(); }, []);
  return ref;
}

/** Bottom of something standing on the whole column (Done, the speed readout), from `base` px above the safe area. */
export const onStack = (base = 16, extra = 0) =>
  `calc(max(${base}px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px) + var(--hud-dock-h, 0px)${extra ? ` + ${extra}px` : ''})`;

/** A dock's max height: its own cap, or what is left once the player bar, a Done row and the top corner are taken. */
export const DOCK_MAX_H = (cap) =>
  `min(${cap}px, calc(100dvh - var(--hud-player-h, 0px) - var(--hud-corner-h, 60px) - 96px))`;

// THE BOTTOM CENTRE, BOTTOM UP (2026-11-01).
//
// Khari, on a screenshot at the Jahva House: "overlap in dialogue" — the
// on-screen WASD keys sat on the performer's caption. The captions stood at a
// guessed 84px over the player bar; the keys stand on the player bar (raised
// over the quick-speak mic while it shows) and are ~180px tall, so the two
// shared the same strip of screen.
//
//   a caption, the street hint   bottom: overKeys()
//   the WASD keys                --hud-keys-top (useTopEdge)
//   the stack (BottomSlot)       --hud-bottom-top (BottomSlot)
//
// Each publishes how high its TOP edge is (px from the bottom of the screen);
// the next one up stands on it, or on its own old spot when that is higher
// (the keys folded to a pill, or not shown).
const EDGE_GAP = 10;

// ONE CHANGE, PASSED UP (2026-11-01). A layer moving without resizing (the player
// bar widening under the centre, the stack lifting onto it) changes nothing a
// ResizeObserver sees, so whatever stands on it kept an old number — Khari: "the
// 'listen together' button overlays the player". Every published edge goes
// through here: a real change sends one `hud:reflow` (at most once a frame), and
// every layer measures again; one that comes out the same sends nothing, so it settles.
let reflowQueued = false;
/** Set (or clear, with null) a HUD variable on :root; a real change asks the layers above to measure again. */
export function setHudVar(name, value) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const cur = root.style.getPropertyValue(name);
  if (value == null) { if (!cur) return; root.style.removeProperty(name); } else { if (cur === value) return; root.style.setProperty(name, value); }
  if (reflowQueued) return;
  reflowQueued = true;
  requestAnimationFrame(() => { reflowQueued = false; window.dispatchEvent(new Event('hud:reflow')); });
}

/** A ref for something others stand on: how high its top edge is becomes `name` while it is mounted. */
export function useTopEdge(name) {
  const node = useRef(null);
  const watch = useRef(null);
  const measure = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (!node.current) { setHudVar(name, null); return; }
    const top = window.innerHeight - node.current.getBoundingClientRect().top;
    setHudVar(name, `${Math.max(0, Math.ceil(top))}px`);
  }, [name]);
  const ref = useCallback((el) => {
    watch.current?.disconnect();
    watch.current = null;
    node.current = el;
    measure();
    if (el && typeof ResizeObserver !== 'undefined') {
      watch.current = new ResizeObserver(measure);
      watch.current.observe(el);
    }
  }, [measure]);
  // It also moves without resizing (raised over the mic, the window resized).
  useLayoutEffect(() => { measure(); });
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('hud:reflow', measure);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('hud:reflow', measure); watch.current?.disconnect(); node.current = null; measure(); };
  }, [measure]);
  return ref;
}

/** Bottom of something over the WASD keys (a caption, a hint): `base` px over the player bar, or above the keys when they reach higher. */
export const overKeys = (base = 84) =>
  `max(calc(max(${base}px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px)), calc(var(--hud-keys-top, 0px) + ${EDGE_GAP}px))`;
